/**
 * [PURPOSE]: Template resolver layer for MAGIC_ITEM_TABLES entries that
 *   reference a CATEGORY of items rather than a specific BASE_ITEMS row.
 *
 *   DMG roll tables include entries like "Weapon, +1" / "Armor, +3" /
 *   "Belt of Hill Giant Strength" — the table tells you the *kind* of
 *   item and any modifier, then rolls for the concrete weapon/armor/etc.
 *   Pre-fix, every variant required its own BASE_ITEMS row
 *   (longsword_plus_1, longsword_plus_2, longsword_plus_3, shortsword_plus_1,
 *   ...). That created a combinatorial explosion AND a maintenance trap:
 *   most weapon types had no +N entry, so a roll like "weapon_plus_2"
 *   resolved to "longsword_plus_2" by accident only because that was one
 *   of the 16 hand-curated rows.
 *
 *   The data-driven shape is: store template metadata in the table entry
 *   ({ template: 'weapon_plus_n', n: 2 }), and resolve at roll-time by
 *   randomly picking a base weapon and attaching the magicalBonus property.
 *   The EditItemDialog already understands magicalBonus (line 851-863) so
 *   the resolved item integrates with effectCalculator without further wiring.
 *
 * [WHY]: Architecture audit (2026-04-30) — "23 of 290 MAGIC_ITEM_TABLES
 *   entries are pseudo-IDs that need a template resolver layer in
 *   shopGenerator.js, NOT new BASE_ITEMS." The existing 16 redundant
 *   *_plus_N rows in gameData.js delete after this lands.
 *
 * [SHAPE]: Each resolver receives the table entry and returns
 *   { id, baseItem, properties }:
 *     - id        — synthetic identifier for this generated instance
 *                   (e.g. "longsword_plus_2"); kept human-readable for logs.
 *     - baseItem  — the BASE_ITEMS row to clone (full definition including
 *                   weight/size/damage/etc. — all metadata preserved per
 *                   Rule #9 in CLAUDE.md).
 *     - properties — extra fields merged onto the resolved inventory item
 *                   (magicalBonus, requiresAttunement, statModifications, …).
 *
 * [INTEGRATION]: shopGenerator.rollFromTable() dispatches on entry.template.
 *   App.jsx generateShopWithD100() honors templates via resolveTemplateEntry()
 *   so both code paths produce the same shape.
 */
import { BASE_ITEMS } from '../data/gameData';

// [WHY EXCLUDE *_plus_*]: The 16 redundant magical weapon/armor rows in
// gameData.js are removed in Commit 2; until then, exclude them from the
// random pool so weapon_plus_2 doesn't accidentally pick longsword_plus_1
// (which would double-stack magicalBonus). After Commit 2 the filter is
// no-op but stays defensive for any future legacy rows.
function _isLegacyPlusN(id) {
  return /_plus_\d+$/.test(id);
}

// [WHY VERSATILE FILTERS]: A "weapon" in the DMG context is something a
// PC wields — i.e. an entry under BASE_ITEMS.weapons that has a damage
// property. That filter excludes shields-as-weapons cases and keeps the
// pool sane.
function _eligibleWeaponIds() {
  const w = BASE_ITEMS.weapons || {};
  return Object.keys(w).filter((id) => {
    if (_isLegacyPlusN(id)) return false;
    const entry = w[id];
    if (!entry || typeof entry.damage !== 'string') return false;
    // [MAY 11 2026 — audit(items) magic-weapons-ammunition]: Exclude
    // already-magical weapons from the "Weapon, +N" template pool. RAW
    // intent is that templates pick a MUNDANE base and apply the +N;
    // rolling Flame Tongue +2 (or layering +N onto an already-silvered
    // longsword) is not in scope. Filter by the data markers the audit
    // pass standardized: requiresAttunement, magicalBonus, effects array,
    // or the silvered flag.
    if (entry.requiresAttunement) return false;
    if (entry.magicalBonus) return false;
    if (Array.isArray(entry.effects) && entry.effects.length > 0) return false;
    if (entry.silvered) return false;
    return true;
  });
}

// Armor entries in BASE_ITEMS.armor that are NOT shields. Shields use
// the separate shield_plus_n template — they share BASE_ITEMS.armor as
// their category container but have a different effect signature
// (acBonus vs ac).
function _eligibleArmorIds() {
  const a = BASE_ITEMS.armor || {};
  return Object.keys(a).filter((id) => {
    if (_isLegacyPlusN(id)) return false;
    const entry = a[id];
    if (!entry) return false;
    // Plain armor entries have an `ac` field; shields use `acBonus`.
    // Templates target wearable armor only.
    if (typeof entry.ac !== 'number') return false;
    if (entry.armorType === 'shield') return false;
    // [MAY 11 2026 — audit(items) magic-armor-shields]: Exclude already-
    // magical armors from the "Armor, +N" template pool (parallel to the
    // weapons audit fix). RAW intent: templates pick a MUNDANE base and
    // apply the +N. The SRD armor section currently contains three
    // conceptually-magical entries (mithral_chain_shirt, adamantine_splint,
    // dragon_scale_mail) that have no magic markers; filter by data flags
    // the audit standardized.
    if (entry.requiresAttunement) return false;
    if (entry.magicalBonus) return false;
    if (Array.isArray(entry.effects) && entry.effects.length > 0) return false;
    return true;
  });
}

// Shield entries in BASE_ITEMS.armor (shield + wooden_shield variants).
// effectCalculator._calculateBaseAC adds +2 for any shield (line 637)
// plus magicalBonus (line 642), so a +N shield contributes (acBonus + N).
function _eligibleShieldIds() {
  const a = BASE_ITEMS.armor || {};
  return Object.keys(a).filter((id) => {
    if (_isLegacyPlusN(id)) return false;
    const entry = a[id];
    if (!entry) return false;
    if (entry.armorType !== 'shield') return false;
    // [MAY 11 2026 — audit(items) magic-armor-shields]: Exclude already-
    // magical shields from the "Shield, +N" template pool. Today BASE_ITEMS.armor
    // only contains the mundane `shield` + `wooden_shield`, but this filter
    // is defensive against future additions and mirrors the armor pool guard.
    if (entry.requiresAttunement) return false;
    if (entry.magicalBonus) return false;
    if (Array.isArray(entry.effects) && entry.effects.length > 0) return false;
    return true;
  });
}

// Ammunition: arrows / bolts / sling bullets. Excludes named entries
// (arrow_slaying, blowgun_needles_poisoned) so "Ammunition +1" rolls
// against generic stacks. Excludes legacy *_plus_N and named-magical
// (already-magical entries shouldn't double up).
function _eligibleAmmunitionIds() {
  const a = BASE_ITEMS.ammunition || {};
  return Object.keys(a).filter((id) => {
    if (_isLegacyPlusN(id)) return false;
    const entry = a[id];
    if (!entry) return false;
    // RAW: ammunition templates apply to arrows / bolts / bullets / needles.
    // Skip pluralized aliases (we want the canonical singular per ARC-014).
    // Skip silvered (already magical-adjacent) and special slaying entries.
    if (id.includes('silvered')) return false;
    if (id.includes('slaying')) return false;
    if (id.includes('poisoned')) return false;
    // Skip plural duplicates. The canonical singular `arrow` covers it.
    if (id === 'arrows' || id === 'crossbow_bolts' || id === 'sling_bullets') return false;
    if (id === 'crossbow_bolt') return false; // canonical is `bolt`
    return true;
  });
}

function _pick(arr) {
  if (!arr || arr.length === 0) return null;
  return arr[Math.floor(Math.random() * arr.length)];
}

// =============================================================================
// [APR 30 2026]: Treasure-roll templates added for Individual Treasure
// (DMG p.135) and Treasure Hoard (DMG p.137-139) tables. Coin rolls
// resolve to a coin pouch with `containsCurrency` so the existing
// inventory machinery (InventoryContext.removeItem deposits the
// currency on remove, App.jsx addItem extracts it on add) handles
// them correctly. Hoard rolls return a composite "treasure trove"
// summary item; players who want each component as a separate
// inventory entry should roll the component tables (Individual
// Treasure, Gemstones, Art Objects) directly.
// =============================================================================

// Roll a "NdM" formula (e.g., "5d6") and return the integer sum. Falls
// back to a single-die interpretation if the formula is just a number
// (e.g., "1" → 1) or malformed (returns 0 with a console warning).
export function _rollDiceFormula(formula) {
  if (formula == null) return 0;
  const str = String(formula).trim();
  // Plain integer (e.g., "1" — used by Hoard magicItems formula).
  if (/^\d+$/.test(str)) return parseInt(str, 10);
  // NdM form.
  const m = str.match(/^(\d+)d(\d+)$/i);
  if (!m) {
    // eslint-disable-next-line no-console
    console.warn(`[templateResolvers] Unrecognized dice formula: ${formula}`);
    return 0;
  }
  const n = parseInt(m[1], 10);
  const d = parseInt(m[2], 10);
  if (!Number.isFinite(n) || !Number.isFinite(d) || n <= 0 || d <= 0) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += Math.floor(Math.random() * d) + 1;
  }
  return sum;
}

// Convert a coin currency code to its gp-equivalent multiplier so
// the shop generator can price hoard summaries. RAW conversions:
//   1 cp = 0.01 gp, 1 sp = 0.1 gp, 1 ep = 0.5 gp, 1 gp = 1 gp,
//   1 pp = 10 gp.
function _coinToGp(currency, amount) {
  switch (currency) {
    case 'cp': return amount * 0.01;
    case 'sp': return amount * 0.1;
    case 'ep': return amount * 0.5;
    case 'gp': return amount;
    case 'pp': return amount * 10;
    default:   return 0;
  }
}

// Currency display names for hoard summaries.
const _COIN_NAMES = {
  cp: 'copper',
  sp: 'silver',
  ep: 'electrum',
  gp: 'gold',
  pp: 'platinum',
};

/**
 * Belt of Giant Strength variants (DMG p.155). RAW:
 *   - Hill Giant     STR 21 — rare       (belt_hill_giant)
 *   - Stone Giant    STR 23 — very rare  (belt_stone_giant)  ┐ identical
 *   - Frost Giant    STR 23 — very rare  (belt_frost_giant)  ┘ in 5e
 *   - Fire Giant     STR 25 — very rare  (belt_fire_giant)
 *   - Cloud Giant    STR 27 — legendary  (belt_cloud_giant)
 *   - Storm Giant    STR 29 — legendary  (belt_storm_giant)
 *
 * Tables F+H reference Hill (rare), Stone-or-Frost (very rare 23, randomized),
 * Cloud (legendary), Storm (legendary). We pick the matching BASE_ITEMS
 * row by strength; the 23-strength case randomizes between stone/frost.
 */
function _resolveBeltByStrength(strength) {
  const rows = BASE_ITEMS.magical_items || {};
  switch (strength) {
    case 21: return rows.belt_hill_giant ? { id: 'belt_hill_giant', baseItem: rows.belt_hill_giant } : null;
    case 23: {
      // 5e RAW: Stone and Frost are identical; randomize for flavor.
      const variant = Math.random() < 0.5 ? 'belt_stone_giant' : 'belt_frost_giant';
      return rows[variant] ? { id: variant, baseItem: rows[variant] } : null;
    }
    case 25: return rows.belt_fire_giant ? { id: 'belt_fire_giant', baseItem: rows.belt_fire_giant } : null;
    case 27: return rows.belt_cloud_giant ? { id: 'belt_cloud_giant', baseItem: rows.belt_cloud_giant } : null;
    case 29: return rows.belt_storm_giant ? { id: 'belt_storm_giant', baseItem: rows.belt_storm_giant } : null;
    default: return null;
  }
}

export const TEMPLATE_RESOLVERS = {
  /**
   * "Weapon, +N" — pick any non-magical weapon and attach magicalBonus.
   *
   * effectCalculator.calculateWeaponAttackBonus() reads weapon.magicalBonus
   * directly (L~1571) — Path 2a, works today without effects array.
   *
   * [SUB-PLAN 1I(d) — 2026-05-26]: ALSO emit canonical S1 `effects:` array
   *   so buildItemRegistrationsFromInventory (Path 1) registers the attack
   *   bonus via effectRegistry. Rule 21: we extend the resolver output, NOT
   *   the BASE_ITEMS pool. Both paths coexist; Path 2a can be deprecated
   *   once S1 is confirmed universal (see Sub-plan 1I(e)).
   *   Effect type: `weaponAttackBonus` (effectRegistry.js L83).
   */
  weapon_plus_n: (entry) => {
    const n = entry?.n ?? 1;
    const ids = _eligibleWeaponIds();
    const pickedId = _pick(ids);
    if (!pickedId) return null;
    const baseItem = BASE_ITEMS.weapons[pickedId];
    return {
      id: pickedId,
      baseItem,
      properties: {
        magicalBonus: n,
        effects: [{ type: 'weaponAttackBonus', operation: 'add', value: n }],
      },
    };
  },

  /**
   * "Armor, +N" — pick any wearable (non-shield) armor and attach magicalBonus.
   * effectCalculator._calculateBaseAC reads armor.magicalBonus directly
   * (L~755) — Path 2a. See note on weapon_plus_n for the Path 1 addition.
   *
   * [SUB-PLAN 1I(d) — 2026-05-26]: ALSO emit canonical S1 `effects:` array.
   *   Effect type: `ac` with operation `add` (effectRegistry.js L35 — ac).
   *   Shields use the shield_plus_n template.
   */
  armor_plus_n: (entry) => {
    const n = entry?.n ?? 1;
    const ids = _eligibleArmorIds();
    const pickedId = _pick(ids);
    if (!pickedId) return null;
    const baseItem = BASE_ITEMS.armor[pickedId];
    return {
      id: pickedId,
      baseItem,
      properties: {
        magicalBonus: n,
        effects: [{ type: 'ac', operation: 'add', value: n }],
      },
    };
  },

  /**
   * "Shield, +N" — pick any shield and attach magicalBonus.
   * Shield entries have acBonus (not ac); the +N is added by
   * effectCalculator on top of the standard +2 shield bonus.
   *
   * [SUB-PLAN 1I(d) — 2026-05-26]: ALSO emit canonical S1 `effects:` array.
   *   Effect type: `ac` with operation `add` — same as armor; effectRegistry
   *   consumer does not distinguish armor vs shield source for AC adds.
   */
  shield_plus_n: (entry) => {
    const n = entry?.n ?? 1;
    const ids = _eligibleShieldIds();
    const pickedId = _pick(ids);
    if (!pickedId) return null;
    const baseItem = BASE_ITEMS.armor[pickedId];
    return {
      id: pickedId,
      baseItem,
      properties: {
        magicalBonus: n,
        effects: [{ type: 'ac', operation: 'add', value: n }],
      },
    };
  },

  /**
   * "Ammunition, +N" — pick any generic ammunition and attach magicalBonus.
   * Resolves to arrow / bolt / sling_bullet (singular canonical IDs per
   * ARC-014). Stack semantics from BASE_ITEMS are preserved.
   *
   * [SUB-PLAN 1I(d) — 2026-05-26]: ALSO emit canonical S1 `effects:` array.
   *   Effect type: `weaponAttackBonus` — ammo +N adds to attack rolls when
   *   the ammo slot is equipped (quiver slot).
   */
  ammunition_plus_n: (entry) => {
    const n = entry?.n ?? 1;
    const ids = _eligibleAmmunitionIds();
    const pickedId = _pick(ids);
    if (!pickedId) return null;
    const baseItem = BASE_ITEMS.ammunition[pickedId];
    return {
      id: pickedId,
      baseItem,
      properties: {
        magicalBonus: n,
        effects: [{ type: 'weaponAttackBonus', operation: 'add', value: n }],
      },
    };
  },

  /**
   * "Belt of Giant Strength" — strength-keyed variant resolver.
   *
   * [SUB-PLAN 1I(d) — 2026-05-26]: The belt BASE_ITEMS rows already carry
   *   canonical S1 `effects:[{type:'ability', target:'str', operation:'set',
   *   value:N, condition:'always'}]` from PHASE 1.3 (NOV 14 2025). When the
   *   resolver output is spread as `{ ...baseItem, ...properties }`, the
   *   baseItem.effects array propagates intact because the prior `properties`
   *   block did NOT include an `effects:` key.
   *
   *   PRIOR BUG: `properties.statModifications: { strengthSet: N }` was the
   *   dead S5 flat-key shape — NO production reader interprets it (per Rule 31
   *   trace `.claude/findings/2026-05-26-datamodel-item-effects-4e7d12.md`
   *   section (b), S5 entry). Removed to clean up the misleading property.
   *
   *   Canonical ability-set effect flows from baseItem.effects via Path 1
   *   (buildItemRegistrationsFromInventory). Legacy Path 3 preserved via
   *   baseItem.statBonus + baseItem.statValue (already on belt BASE_ITEMS rows).
   */
  belt_of_giant_strength: (entry) => {
    const strength = entry?.strength;
    const resolved = _resolveBeltByStrength(strength);
    if (!resolved) return null;
    return {
      id: resolved.id,
      baseItem: resolved.baseItem,
      properties: {
        requiresAttunement: true,
        // [SUB-PLAN 1I(d)]: S5 `statModifications: { strengthSet: N }` REMOVED.
        // Canonical S1 effect flows from baseItem.effects (present on all belt
        // BASE_ITEMS rows since PHASE 1.3). No resolver-level override needed.
      },
    };
  },

  /**
   * "Dragon Scale Mail" — randomly picks one of the 10 color variants
   *   from BASE_ITEMS.armor. Each variant grants +1 AC, resistance to
   *   its dragon color's damage type, and advantage on saves vs.
   *   Frightful Presence + breath weapons (DMG p.165).
   *
   *   Pool: dragon_scale_mail_black, _blue, _brass, _bronze, _copper,
   *         _gold, _green, _red, _silver, _white (Spec 2 / 2026-05-15).
   */
  dragon_scale_mail: () => {
    const DSM_IDS = [
      'dragon_scale_mail_black',
      'dragon_scale_mail_blue',
      'dragon_scale_mail_brass',
      'dragon_scale_mail_bronze',
      'dragon_scale_mail_copper',
      'dragon_scale_mail_gold',
      'dragon_scale_mail_green',
      'dragon_scale_mail_red',
      'dragon_scale_mail_silver',
      'dragon_scale_mail_white',
    ];
    const pickedId = _pick(DSM_IDS);
    if (!pickedId) return null;
    const baseItem = BASE_ITEMS.armor[pickedId];
    if (!baseItem) return null;
    return {
      id: pickedId,
      baseItem,
      properties: {
        requiresAttunement: true,
      },
    };
  },

  /**
   * "Ring of Elemental Command" — picks one of the 4 element variants
   *   (Air / Earth / Fire / Water) per DMG p.190. RAW gives no weights so
   *   uniform across the 4. All variants are legendary + attunement.
   *
   *   Used by magicItemTables.js table_h roll 27-29 ("Ring of Elemental
   *   Command" generic). The variant-specific entries Fire and Water exist
   *   on their own rolls in older tables; Air/Earth are newly added and
   *   only reachable through this resolver (Spec 2 / 2026-05-15).
   */
  ring_of_elemental_command: () => {
    const REC_IDS = [
      'ring_of_elemental_command_air',
      'ring_of_elemental_command_earth',
      'ring_of_elemental_command_fire',
      'ring_of_elemental_command_water',
    ];
    const pickedId = _pick(REC_IDS);
    if (!pickedId) return null;
    const baseItem = BASE_ITEMS.magical_items[pickedId];
    if (!baseItem) return null;
    return {
      id: pickedId,
      baseItem,
      properties: {
        requiresAttunement: true,
      },
    };
  },

  /**
   * "Coin Pouch" — Individual Treasure tables (DMG p.135) and the
   *   coins component of Treasure Hoard tables (DMG p.137-139). Rolls
   *   the dice formula, multiplies by the table's order-of-magnitude
   *   multiplier, and produces a coin pouch with `containsCurrency`
   *   set so InventoryContext deposits the currency on inventory add.
   *
   * Args:
   *   - currency: 'cp' | 'sp' | 'ep' | 'gp' | 'pp'
   *   - formula:  'NdM' string (e.g., '5d6')
   *   - multiplier: integer (default 1; e.g., 100 for "× 100")
   */
  coin: (entry) => {
    const currency = entry?.currency;
    const formula = entry?.formula;
    const multiplier = entry?.multiplier ?? 1;
    if (!currency || !formula) return null;
    if (!_COIN_NAMES[currency]) return null;
    const baseRoll = _rollDiceFormula(formula);
    const amount = baseRoll * multiplier;
    if (amount <= 0) return null;

    // Use the existing pouch BASE_ITEM as the resolved base. This ensures
    // the resulting item has weight/size/icon/containerSlots set and
    // integrates cleanly with the inventory grid.
    const pouchBase = (BASE_ITEMS.containers && BASE_ITEMS.containers.pouch) || null;
    if (!pouchBase) return null;

    const coinName = _COIN_NAMES[currency];
    const id = `coin_pouch_${currency}_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
    return {
      id,
      // Spread pouch metadata (weight, size, icon, containerSlots, maxWeight)
      // so the resolved item is fully usable on the grid. Override icon
      // with a coin-themed glyph to differentiate from a plain pouch.
      baseItem: { ...pouchBase, icon: '💰', baseValue: _coinToGp(currency, amount) },
      properties: {
        containsCurrency: { type: currency, amount },
        // Description surfaces what the pouch holds; the canonical
        // amount lives in `containsCurrency`.
        description: `Pouch containing ${amount} ${coinName} pieces (${currency})`,
        // Mark the resolved item as a pouch so consumers can distinguish
        // it from a regular container (e.g., not pickable for storage).
        isCoinPouch: true,
      },
    };
  },

  /**
   * "Treasure Hoard" — DMG p.137-139 composite roll. Rolls the table-
   *   level fixed coin pile, the optional gem batch, the optional art
   *   batch, and rolls N magic items from the referenced Magic Item
   *   Table. Returns a single summary "treasure trove" item whose
   *   `containsCurrency` totals all coins as gp-equivalent and whose
   *   description lists each component for the DM's reference.
   *
   *   Magic-item rolls are NOT resolved here (they'd require importing
   *   MAGIC_ITEM_TABLES, creating a circular dependency between
   *   templateResolvers.js and magicItemTables.js). Instead, the
   *   description references the table by name (e.g., "1× roll on
   *   DMG Magic Item Table A") and stores the structured plan under
   *   `properties.hoardPlan` so a future decompose-UI can consume it.
   *
   * Args:
   *   - cr:    label like '0-4' (cosmetic, used in description)
   *   - coins: [{currency, formula, multiplier}, ...] — fixed coin pile
   *   - gems:  { tier, formula }                       — optional
   *   - art:   { tier, formula }                       — optional
   *   - magicItems: [{ table, formula }, ...]          — optional
   */
  hoard: (entry) => {
    const cr = entry?.cr || '?';
    const coins = Array.isArray(entry?.coins) ? entry.coins : [];
    const gems = entry?.gems || null;
    const art = entry?.art || null;
    const magicItems = Array.isArray(entry?.magicItems) ? entry.magicItems : [];

    // Roll the fixed coin pile.
    const coinTotals = coins.map((c) => {
      const roll = _rollDiceFormula(c.formula);
      const amount = roll * (c.multiplier ?? 1);
      return { currency: c.currency, amount };
    });

    // Aggregate gp value across all coin totals (so the hoard has a
    // single sortable baseValue).
    let totalGp = 0;
    for (const ct of coinTotals) {
      totalGp += _coinToGp(ct.currency, ct.amount);
    }

    // Roll gem batch if specified.
    const gemRoll = gems ? { tier: gems.tier, count: _rollDiceFormula(gems.formula) } : null;
    if (gemRoll) totalGp += gemRoll.tier * gemRoll.count;

    // Roll art batch if specified.
    const artRoll = art ? { tier: art.tier, count: _rollDiceFormula(art.formula) } : null;
    if (artRoll) totalGp += artRoll.tier * artRoll.count;

    // Magic-item rolls — resolve count only; the table name is stored
    // for the decompose-UI to interpret later.
    const magicRolls = magicItems.map((m) => ({
      table: m.table,
      count: _rollDiceFormula(m.formula),
    }));

    // Use the chest BASE_ITEM as the resolved base. A treasure trove is
    // typically too big for a pouch.
    const chestBase =
      (BASE_ITEMS.containers && BASE_ITEMS.containers.chest) ||
      (BASE_ITEMS.containers && BASE_ITEMS.containers.sack) ||
      null;
    if (!chestBase) return null;

    // Build a human-readable description listing every component.
    const lines = [`Treasure Hoard (CR ${cr}). Total value approx. ${Math.round(totalGp)} gp.`];
    if (coinTotals.length > 0) {
      const coinLine = coinTotals
        .filter((ct) => ct.amount > 0)
        .map((ct) => `${ct.amount} ${_COIN_NAMES[ct.currency]}`)
        .join(', ');
      if (coinLine) lines.push(`Coins: ${coinLine}.`);
    }
    if (gemRoll && gemRoll.count > 0) {
      lines.push(`Gems: ${gemRoll.count} × ${gemRoll.tier} gp gems.`);
    }
    if (artRoll && artRoll.count > 0) {
      lines.push(`Art: ${artRoll.count} × ${artRoll.tier} gp art objects.`);
    }
    for (const mr of magicRolls) {
      if (mr.count > 0) {
        lines.push(`Magic Items: roll ${mr.count}× on ${mr.table.replace('table_', 'DMG Table ').toUpperCase()}.`);
      }
    }

    const id = `treasure_hoard_${cr.replace(/\W+/g, '_')}_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
    return {
      id,
      baseItem: { ...chestBase, icon: '🏴‍☠️', baseValue: Math.round(totalGp) },
      properties: {
        // For the gp portion, expose containsCurrency so the inventory
        // currency-on-add path picks it up. Other components (gems,
        // art, magic items) are listed in description + hoardPlan.
        containsCurrency:
          totalGp > 0
            ? { type: 'gp', amount: Math.round(totalGp) }
            : undefined,
        description: lines.join('\n'),
        isHoardSummary: true,
        // Structured plan for a future decompose-UI ticket.
        hoardPlan: {
          cr,
          coins: coinTotals,
          gems: gemRoll,
          art: artRoll,
          magicItems: magicRolls,
        },
      },
    };
  },
};

/**
 * Resolve a roll-table entry into a concrete item. Returns:
 *   - { id, baseItem, properties, name, rarity, sourceEntry }  on hit
 *   - null  on miss (no resolver, no BASE_ITEMS pool, etc.)
 *
 * Non-template entries (the common case — entry.item is a BASE_ITEMS ID)
 * pass through with baseItem = null so the caller can use its own
 * getItemDefinition() lookup. This keeps the dispatcher's contract
 * minimal: callers detect the "template took over" case via
 * (resolved && resolved.baseItem != null).
 *
 * @param {object} entry - A MAGIC_ITEM_TABLES roll entry.
 * @returns {object|null}
 */
export function resolveTemplateEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const { template } = entry;
  if (!template) return null; // Caller handles flat-ID entries.
  const resolver = TEMPLATE_RESOLVERS[template];
  if (typeof resolver !== 'function') return null;
  const resolved = resolver(entry);
  if (!resolved) return null;
  return {
    ...resolved,
    name: entry.name || resolved.id,
    rarity: entry.rarity,
    sourceEntry: entry,
  };
}
